import React, { Suspense, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { actions } from 'slices/app.slice'
import Fallback from 'components/Fallback'
import styles from 'index.module.scss'
import { Image } from 'react-bootstrap'
import { adTags, rememberLandingParams, storedLandingParams } from 'utils/metaEvents'
import { images } from './theme'
import { auth, database } from './utils/firebase'
import { detectVariant, storeVariant } from './utils/variant'
import { resumeChat, takeResumeToken } from './utils/chatResume'

const Auth = React.lazy(() => import('./pages/auth'))
const Dashboard = React.lazy(() => import('./pages/dashboard'))
const CompletePayment = React.lazy(() => import('./pages/CompletePayment'))

function Router(props) {
  const dispatch = useDispatch()
  const { checked, loggedIn, variant: appVariant } = useSelector((state) => state.app)

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search || props.urlParams)

    let utmParams = {};
    urlParams.forEach((value, key) => {
      if (key !== 'source' && key !== 'environment' && key !== 'user_id' && key !== 'medical_issue' && key !== 'ctx') {
        utmParams[key] = value;
      }
    });

    if (Object.keys(utmParams).length <= 0) {
      utmParams = null;
    }
    // Care: keep the tab's latest ad tags (utm_*, click ids) across reloads, and restore them when a
    // reloaded URL has no ad tags, so they still reach users/{uid}/utmParams at sign-up (utils/metaEvents.js).
    rememberLandingParams(utmParams)
    if (!adTags(utmParams)) {
      const kept = storedLandingParams()
      if (kept) utmParams = { ...(utmParams || {}), ...kept }
    }
    const key = (utmParams && (utmParams.gclid || utmParams.gbraid || utmParams.wbraid || utmParams.gclsrc)) || null;
    const variant = detectVariant(window.location.pathname)
    if (variant) {
      storeVariant(variant)
      dispatch(actions.setVariant(variant))
    }
    const source = urlParams.get('source') || null
      const environment = urlParams.get('environment') || null
      const user_id = urlParams.get('user_id') || null
      const medicalIssue = (urlParams.get('medical_issue') || '').trim()
      if (medicalIssue) {
        try { sessionStorage.setItem('ydo_medical_issue', medicalIssue) } catch (e) {}
      }
      // public/index.html moves ?ctx= out of the address before the trackers load and keeps it in
      // window.__ydoHandoffCtx (this page load) and sessionStorage ydo_handoff_ctx as {code, at}
      // (survives a reload during a slow or failed fetch); the URL read is the fallback if that script
      // failed. The page copy is dropped at once so no other script on the page can pick the code up
      // later. A stored code older than 30 minutes is ignored, so a handoff that failed long ago can't
      // prefill or attribute a later, unrelated visit in the same tab.
      let storedCtx = null
      try {
        const kept = JSON.parse(window.sessionStorage.getItem('ydo_handoff_ctx') || 'null')
        if (kept && typeof kept.code === 'string' && Date.now() - kept.at < 30 * 60 * 1000) storedCtx = kept.code
      } catch (e) { /* storage blocked or unreadable */ }
      const clearStoredCtx = () => {
        try { window.sessionStorage.removeItem('ydo_handoff_ctx') } catch (e) { /* storage blocked */ }
      }
      const ctxToken = (urlParams.get('ctx') || window.__ydoHandoffCtx || storedCtx || '').trim()
      delete window.__ydoHandoffCtx
      if (ctxToken && ctxToken.length <= 256) {
        // 10s timeout so a slow / unreachable handoff endpoint can't
        // sit in the request queue for the browser's default several
        // minutes. Generous enough to absorb Cloud Function cold
        // starts (1-3s) on top of a slow mobile connection. The
        // prefill is best-effort — on timeout we abort and the
        // funnel proceeds without it.
        //
        // Endpoint base URL is env-driven so staging and prod can
        // point at different patient-MCP deployments. Defaults to
        // staging if the env var is missing -- safer for CI / raw
        // react-scripts invocations than defaulting to prod.
        const handoffBase = process.env.REACT_APP_HANDOFF_BASE_URL || 'https://stg-connect.yourdoctors.online'
        const ctrl = new AbortController()
        const timeoutId = setTimeout(() => ctrl.abort(), 10000)
        fetch(`${handoffBase}/api/handoff/${encodeURIComponent(ctxToken)}`, { signal: ctrl.signal })
          .then((res) => {
            // The code is settled by a readable answer or by not_found_or_expired (404/410); a network
            // failure, timeout, 5xx or 429 keeps it for a retry on reload.
            if (res.status === 404 || res.status === 410) clearStoredCtx()
            return res.ok ? res.json() : null
          })
          .then((data) => {
            if (!data) return
            clearStoredCtx()
            const issue = typeof data.medical_issue === 'string' ? data.medical_issue.trim() : ''
            if (issue) {
              try { sessionStorage.setItem('ydo_medical_issue', issue) } catch (e) {}
            }
            // Stash the handoff-token mint time for funnel attribution.
            // setSocialInfo picks this up at signup and writes it to
            // users/{uid}/mcpHandoffAt in RTDB. Epoch milliseconds
            // (the patient-MCP team's contract — not seconds).
            if (typeof data.created_at === 'number' && Number.isFinite(data.created_at)) {
              try { sessionStorage.setItem('ydo_handoff_created_at', String(data.created_at)) } catch (e) {}
            }
            // Backend source fallback: if the URL didn't include
            // ?source=, fall back to the source from the handoff
            // response. Defensive against tool calls that build the
            // URL without the source param.
            //
            // Coverage: updates state.app.source, so downstream
            // useSelector consumers (Signup, downloadApp) see the
            // value. The signup-path RTDB attribution in
            // setSocialInfo (app.slice.js:167,206) reads
            // getState().app.source fresh and DOES pick up the
            // fallback — users/{uid}/sources/{source} lands correctly
            // when the user completes signup via social auth.
            //
            // Limitation: the older RTDB write inside authenticate's
            // onAuthStateChanged callback (app.slice.js:429,435)
            // captures the `source` argument at call time, so a
            // reload after auth without re-signup will miss the
            // fallback for that specific write. Spec says LLM
            // tool-call URLs always carry ?source=, so this gap is
            // moot for compliant tool callers.
            if (!source && typeof data.source === 'string' && data.source) {
              dispatch(actions.setSource(data.source))
            }
          })
          .catch((e) => { console.log('ctx handoff error', e) })
          .finally(() => clearTimeout(timeoutId))
      }
    // A care resume link (/resume?t=, BUG-011) signs the patient in first; the splash shows meanwhile
    // because `checked` stays false until authenticate runs. The usual start then opens their chat.
    const startAuth = () => dispatch(actions.authenticate(source, utmParams, {environment, user_id}))
    // Care builds only: register keeps its own behaviour at /resume (review 1, issue 7). Same build-time test as
    // public/index.html, so a register tab that entered through /b doesn't redeem a token index.html left alone.
    const resumeToken = process.env.REACT_APP_DEFAULT_VARIANT === 'b' ? takeResumeToken() : null
    if (resumeToken) {
      resumeChat(resumeToken, { signIn: (t) => auth.signInWithCustomToken(t) }).then((r) => {
        if (r === 'signed-in') startAuth()
      })
    } else {
      startAuth()
    }
    try{
      if (key){
        let updates = {};
        if (source){
          updates[`googleAds/${key}/source`] = source;
        }
        const utmKeys = Object.keys(utmParams);
        utmKeys.forEach((utmKey) => {
          updates[`googleAds/${key}/${utmKey}`] = utmParams[utmKey];
        });
        updates[`googleAds/${key}/updated_at`] = Date.now();
        if (Object.keys(updates).length > 0){
            database.ref().update(updates).then(data => {console.log('googleAds updated!');
            }).catch((error) => {
                console.log('googleAds error', error?.data || error);
            });
        }
      }
    }
    catch (e){
      console.log("error in uploadUsersUpdate", e)
    }
  }, [])

  var options = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  };
  function success(pos) {
    const crd = pos.coords;
    dispatch(actions.setLocationInfo(crd ? { latitude:crd.latitude, longitude: crd.longitude, accuracy: crd.accuracy } : null))

    console.log("Your current position is:", crd);
    console.log(`Latitude : ${crd.latitude}`);
    console.log(`Longitude: ${crd.longitude}`);
    console.log(`More or less ${crd.accuracy} meters.`);
  }

  function errors(err) {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  useEffect(() => {
    // Care (flow B) doesn't ask for the device location (owner, 2026-11-03: the prompt on page open was annoying).
    // The coordinates only refined the sign-up address guess in setSocialInfo, which also guesses from the IP address
    // and fills only fields the patient left empty. Flow A (register) still asks.
    if (appVariant === 'b') return

    if (navigator && navigator.geolocation) {
      if (navigator.permissions){
        navigator.permissions
            .query({ name: "geolocation" })
            .then(function (result) {
              console.log(result);
              if (result.state === "granted") {
                //If granted then you can directly call your function here
                navigator.geolocation.getCurrentPosition(success, errors, options);
              } else if (result.state === "prompt") {
                //If prompt then the user will be asked to give permission
                navigator.geolocation.getCurrentPosition(success, errors, options);
              } else if (result.state === "denied") {
                //If denied then you have to show instructions to enable location
              }
            });
      }

    } else {
      console.log("Geolocation is not supported by this browser.");
    }
  }, [])

  if (!checked && appVariant === 'b') {
    // Care splash on paper. iOS Safari 26 colours its top and bottom bars from a full-screen fixed
    // element and kept the coral of the splash below on every care page (owner phone test 2026-10-18).
    return (
      <div className={styles.splashB} data-testid="splash">
        <img src="/ydo-logo.svg" alt="" width="56" height="56" />
        <span className={styles.splashBText}>Your Doctors Online</span>
      </div>
    )
  }

  if (!checked) {
    return (
      <div className={styles.background} data-testid="splash">
        <div className={styles.logo}>

          <Image
            src={images.ydoLogoWhite}
            alt="notification"
            priority
          />
          <span className={styles.text}>Your Doctors Online</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <BrowserRouter>
        <Suspense fallback={<Fallback />}>
          {!loggedIn ? (
            <Routes>
              <Route path="/complete-payment" element={<CompletePayment />} />
              <Route path="/auth/*" element={<Auth />} />
              <Route path="*" element={<Auth />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/complete-payment" element={<CompletePayment />} />
              <Route path={"*"} element={<Dashboard />} />
            </Routes>
          )}
        </Suspense>
      </BrowserRouter>
    </div>
  )
}

export default Router
